import { Inject, Injectable } from '@angular/core';
import { DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwOrmDataServiceToken, DwQueryBuilderBase, DwOrmData, DwItemAttribute } from '@devwareapps/devware-cap';
import { defer, Observable, throwError } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AppMetaData, UserProfileEntity, ChallengeTeamEntity } from '../../../../meta-data/app-meta-data.service';
import { canonicalSlug } from './profile-links';

export class ProfileRouteError extends Error {
    constructor(public code: 'schema_pending' | 'not_found' | 'ambiguous', message: string) { super(message); }
}
export interface UserProfileIdentity { UserProfileId: number; UserId: number; ProfileSlug: string; }
export interface TeamProfileIdentity { ChallengeTeamId: number; ChallengeDefinitionId: number; TeamSlug: string; IsPrivateTeam: boolean; }

/** Resolves identity only; it does not grant permission to view or edit a profile. */
@Injectable({ providedIn: 'root' })
export class ProfileRouteResolverService {
    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private metadata: DwMetaDataService) {}
    resolveUser(slug: string): Observable<UserProfileIdentity> {
        return defer(() => {
            const query = AppMetaData.UserProfile.CreateQueryBuilder()
                .addFields(a => [a.UserProfileId, a.UserId, a.ProfileSlug])
                .addFilterAnd(f => f.Equal(a => a.ProfileSlug, canonicalSlug(slug)));
            return this.requireUnique<UserProfileEntity>(query, AppMetaData.UserProfile.ItemDetail.itemName,
                AppMetaData.UserProfile.Attributes.ProfileSlug).pipe(map(row => ({
                UserProfileId: row.UserProfileId, UserId: row.UserId, ProfileSlug: row.ProfileSlug
            })));
        });
    }
    resolveTeam(challengeId: number, slug: string): Observable<TeamProfileIdentity> {
        return defer(() => {
            if (!Number.isSafeInteger(challengeId) || challengeId <= 0 || challengeId > 2147483647) { throw new Error('Invalid challenge link.'); }
            const query = AppMetaData.ChallengeTeam.CreateQueryBuilder()
                .addFields(a => [a.ChallengeTeamId, a.ChallengeDefinitionId, a.TeamSlug, a.IsPrivateTeam])
                .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
                .addFilterAnd(f => f.Equal(a => a.TeamSlug, canonicalSlug(slug)));
            return this.requireUnique<ChallengeTeamEntity>(query, AppMetaData.ChallengeTeam.ItemDetail.itemName,
                AppMetaData.ChallengeTeam.Attributes.TeamSlug).pipe(map(row => ({
                ChallengeTeamId: row.ChallengeTeamId, ChallengeDefinitionId: row.ChallengeDefinitionId,
                TeamSlug: row.TeamSlug, IsPrivateTeam: row.IsPrivateTeam
            })));
        });
    }
    private requireUnique<T extends DwOrmData>(query: DwQueryBuilderBase<T>, item: string, slug: DwItemAttribute): Observable<T> {
        return this.metadata.getMetaData(item).pipe(take(1), switchMap(metadata => {
            if (!metadata?.Attributes?.some(attribute => attribute.AttributeName === slug.fieldName)) {
                return throwError(new ProfileRouteError('schema_pending', 'Profile links are awaiting database and metadata setup.'));
            }
            return this.orm.executeQuery<T>(query).pipe(take(1), map(rows => {
            if (!rows?.length) { throw new ProfileRouteError('not_found', 'Profile unavailable.'); }
            if (rows.length !== 1) { throw new ProfileRouteError('ambiguous', 'Profile link is not unique.'); }
            return rows[0];
            }));
        }));
    }
}
